import { useState, useRef } from "react";
import { PlusIcon, XMarkIcon } from "@heroicons/react/20/solid";
import InputWithVariablePicker from "../../components/InputWithVariablePicker";
import ChartLoading from "../../../insight/components/ChartLoading";
import type { RuleItemEditorBaseProps } from "../ruleItemEditorTypes";

/** Aligns with API: AIFieldOperatorHandler + SummarizeRecordActionData, etc. */
export type AIFieldEditorData = {
  aiFieldAction: string;
  questions?: { question: string; points?: number }[];
  guidance?: string;
  targetAudience?: string;
  tags?: string[];
  allowMultiple?: boolean;
  text?: string;
  prompt?: string;
  maxTokens?: number;
};

type Props = RuleItemEditorBaseProps & { data: AIFieldEditorData };

const addBtnCss =
  "inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 transition-colors w-fit";

/** Step context variables (previous step output). Match backend TemplateService.getActionContext. */
const STEP_CONTEXT_VARIABLES = [
  "previousStepFirstResult",
  "previousStep.output.aiFieldResults",
  "previousStep.output.newValue",
];

/** Pre-defined target audience options for Summarize record. */
const TARGET_AUDIENCE_PRESETS = ["sales reps", "customer success managers"] as const;

// --- Reusable sub-components ---

/** Label + optional description */
function FieldLabel({ label, description }: { label: string; description?: string }) {
  return (
    <>
      <label className="text-sm font-medium text-gray-700">{label}</label>
      {description && <p className="text-xs text-gray-500">{description}</p>}
    </>
  );
}

/** Toggle switch with label */
function ToggleSwitch({
  checked,
  label,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  label: string;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
          checked ? "bg-blue-500" : "bg-gray-200"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={onChange}
        disabled={disabled}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? "translate-x-4" : "translate-x-0"
          }`}
        />
      </button>
      <label className="text-sm text-gray-700">{label}</label>
    </div>
  );
}

// --- Main component ---

export default function RuleItemEditorAIField({
  ruleItem,
  updateRuleItem,
  fields,
  readonly,
  t,
  data,
  attributes,
}: Props) {
  const aiAction = data.aiFieldAction;

  // --- Helper to update data without repeating spread ---
  const updateData = (patch: Record<string, any>) => {
    updateRuleItem({
      ...ruleItem,
      data: { ...ruleItem.data, ...patch },
    });
  };

  // --- Shared InputWithVariablePicker props (no projectId = no contact/account search) ---
  const variablePickerProps = {
    readOnly: readonly,
    attributes,
    extraVariables: STEP_CONTEXT_VARIABLES,
  };

  // --- Questions (researchRecord) ---
  // Support both old format (string[]) and new format ({ question: string; points?: number }[])
  const questions: { question: string; points?: number }[] = (data.questions ?? []).map(
    (q: any) => {
      if (typeof q === "string") {
        return { question: q, points: 10 };
      }
      return { question: q.question || "", points: q.points ?? 10 };
    }
  );

  const addQuestion = () => {
    if (readonly) return;
    updateData({ questions: [...questions, { question: "", points: 10 }] });
  };

  const updateQuestion = (index: number, value: string) => {
    if (readonly) return;
    const updated = [...questions];
    updated[index] = { ...updated[index], question: value };
    updateData({ questions: updated });
  };

  const removeQuestion = (index: number) => {
    if (readonly) return;
    updateData({ questions: questions.filter((_, i) => i !== index) });
  };

  // --- Tags (classifyText / classifyRecord) ---
  const tags: string[] = (data.tags ?? []).filter((tag: string) => tag !== "");
  const [tagInput, setTagInput] = useState("");
  const tagInputRef = useRef<HTMLInputElement>(null);

  const addTagFromInput = () => {
    if (readonly) return;
    const value = tagInput.trim();
    if (!value || tags.includes(value)) {
      setTagInput("");
      return;
    }
    updateData({ tags: [...tags, value] });
    setTagInput("");
  };

  const removeTag = (index: number) => {
    if (readonly) return;
    updateData({ tags: tags.filter((_: string, i: number) => i !== index) });
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTagFromInput();
    } else if (e.key === "Backspace" && tagInput === "" && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  // --- Loading guard ---
  if (!fields || fields.size === 0) {
    return <ChartLoading height={80} label={t("Loading...")} />;
  }

  // --- Section renderers ---

  /** Research Record: Questions list */
  const renderResearchRecord = () => (
    <>
      <div className="flex flex-col gap-1.5">
        <FieldLabel
          label={t("Questions")}
          description={t("Define the questions AI will research for each record")}
        />
        <div className="flex flex-col gap-2">
          {questions.map((q: { question: string; points?: number }, i: number) => (
            <div key={`question_${i}`} className="flex items-start gap-2 group">
              <InputWithVariablePicker
                value={q.question}
                onChange={(value: string) => updateQuestion(i, value)}
                placeholder={t("Enter a research question...")}
                {...variablePickerProps}
              />
              {!readonly && (
                <button
                  type="button"
                  className="shrink-0 p-1.5 mt-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                  onClick={() => removeQuestion(i)}
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        {!readonly && (
          <button type="button" className={`${addBtnCss} mt-2`} onClick={addQuestion}>
            <PlusIcon className="w-3.5 h-3.5" />
            <span>{t("Add question")}</span>
          </button>
        )}
      </div>
    </>
  );

  /** Summarize Record: guidance and target audience for pain-point summary */
  const renderSummarizeRecord = () => {
    const currentAudience = (data.targetAudience ?? "").trim();
    return (
      <>
        <div className="flex flex-col gap-1.5">
          <FieldLabel
            label={t("Target audience")}
            description={t(
              "Who the pain points are for. Pick one or type a custom value. Use variables like {{workspace.name}} if needed."
            )}
          />
          <div className="flex flex-wrap gap-1.5">
            {TARGET_AUDIENCE_PRESETS.map((preset) => {
              const isSelected = currentAudience === preset;
              return (
                <button
                  key={preset}
                  type="button"
                  disabled={!!readonly}
                  onClick={() => updateData({ targetAudience: preset })}
                  className={`inline-flex items-center rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${
                    isSelected
                      ? "bg-blue-100 text-blue-800 ring-1 ring-blue-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  } ${readonly ? "cursor-default opacity-70" : ""}`}
                >
                  {preset}
                </button>
              );
            })}
          </div>
          <InputWithVariablePicker
            value={data.targetAudience ?? ""}
            onChange={(value: string) => updateData({ targetAudience: value })}
            placeholder={t("e.g. sales reps, customer success managers")}
            {...variablePickerProps}
          />
        </div>
        <div className="flex flex-col gap-1">
          <FieldLabel
            label={t("Guidance")}
            description={t(
              "Custom prompt for AI (e.g. what to emphasize). Use {{previousStepFirstResult}} for output from the previous action."
            )}
          />
          <InputWithVariablePicker
            value={data.guidance ?? ""}
            onChange={(value: string) => updateData({ guidance: value })}
            placeholder={t("e.g. Include key metrics and next steps")}
            {...variablePickerProps}
          />
        </div>
      </>
    );
  };

  /** Prompt Completion: Prompt input only */
  const renderPromptCompletion = () => (
    <div className="flex flex-col gap-1">
      <FieldLabel
        label={t("Prompt")}
        description={t(
          "Input prompt for the AI to complete. Use {{previousStepFirstResult}} for output from the previous action."
        )}
      />
      <InputWithVariablePicker
        value={data.prompt ?? ""}
        onChange={(value: string) => updateData({ prompt: value })}
        placeholder={t("Enter prompt for AI to complete")}
        {...variablePickerProps}
      />
    </div>
  );

  /** Classify Text: Input (same as Guidance - text with variable picker) */
  const renderClassifyTextSource = () => (
    <div className="flex flex-col gap-1">
      <FieldLabel
        label={t("Input")}
        description={t(
          "Text to classify. Use variables for field values (e.g. {{sale.description}}) or {{previousStepFirstResult}} from the previous action."
        )}
      />
      <InputWithVariablePicker
        value={data.text ?? ""}
        onChange={(value: string) => updateData({ text: value })}
        placeholder={t("e.g. {{sale.description}} or enter text to classify")}
        {...variablePickerProps}
      />
    </div>
  );

  /** Tags input (shared by classifyText and classifyRecord) */
  const renderTagsSection = () => (
    <>
      <div className="flex flex-col gap-1.5">
        <FieldLabel
          label={t("Tags")}
          description={t("Define the tags AI can assign to classify the content")}
        />
        <div
          className="flex flex-wrap items-center gap-1.5 min-h-[38px] rounded-md border border-gray-300 px-2 py-1.5 cursor-text focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500"
          onClick={() => tagInputRef.current?.focus()}
        >
          {tags.map((tag: string, i: number) => (
            <span
              key={`tag_${i}`}
              className="inline-flex items-center gap-1 rounded-md bg-blue-50 text-blue-700 text-sm px-2 py-0.5"
            >
              {tag}
              {!readonly && (
                <button
                  type="button"
                  className="text-blue-400 hover:text-blue-700 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(i);
                  }}
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          ))}
          {!readonly && (
            <input
              ref={tagInputRef}
              type="text"
              className="flex-1 min-w-[80px] border-0 p-0 text-sm focus:outline-none focus:ring-0 bg-transparent"
              placeholder={tags.length === 0 ? t("Type and press Enter to add tags...") : ""}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              onBlur={addTagFromInput}
            />
          )}
        </div>
      </div>
      <ToggleSwitch
        checked={!!data.allowMultiple}
        label={t("Allow multiple tags")}
        onChange={() => !readonly && updateData({ allowMultiple: !data.allowMultiple })}
        disabled={!!readonly}
      />
    </>
  );

  // --- Render ---
  return (
    <div className="flex flex-col gap-3 w-full max-w-xl">
      {aiAction === "researchRecord" && renderResearchRecord()}
      {aiAction === "summarizeRecord" && renderSummarizeRecord()}
      {aiAction === "promptCompletion" && renderPromptCompletion()}
      {aiAction === "classifyText" && renderClassifyTextSource()}
      {(aiAction === "classifyText" || aiAction === "classifyRecord") && renderTagsSection()}
    </div>
  );
}
